import { shiftMonth } from './iso-week'

/**
 * Leitura do SGI (sgi.flexibase.com.br) — indicadores mensais por área.
 *
 * O SGI mora no mesmo Supabase do dev01 (`public.sgi_*`) e usa o mesmo login.
 * Não tem "responsável" por indicador nem periodicidade: todo indicador é
 * mensal e pertence a uma categoria (`sgi_categories`, com `area = 'Supply'`
 * para Gestão Supply, PCP, Compras, Manutenção e Estoque). O Supply liga a
 * categoria à própria área pelo nome (`supplyAreaCodeOfCategory`), e o
 * responsável da área no Supply é quem responde pelos indicadores dela.
 *
 * As regras de "devido / pendente / atrasado" são as da tela do SGI
 * (`dateUtils.ts`, `launchStatsScope.ts` e `indicatorGoal.ts` do repo SGI),
 * espelhadas aqui e testadas (`sgi.test.ts`). Mudou lá, mude aqui.
 */

export const SGI_URL = 'https://sgi.flexibase.com.br'

/** O SGI não tem rota por indicador: o link abre o sistema (a aba Lançar fica lá dentro). */
export const SGI_LAUNCH_URL = `${SGI_URL}/`

/** Meses antes deste nunca contam como atrasados (`OVERDUE_START_MONTH` do SGI). */
export const SGI_OVERDUE_START_MONTH = '2026-01'

/**
 * Janela de cobrança do Supply (decisão do Juan, 2026-10-21): só os últimos 3
 * meses fechados (o de referência e os 2 anteriores) contam como pendente ou
 * atrasado. Mês mais antigo sem valor aparece como "Sem lançamento", sem cobrar.
 */
export const SGI_CHARGE_MONTHS = 3

export type SgiFocusType = 'crescente' | 'decrescente' | 'margem' | 'eixo'
export type SgiMarginMode = 'range' | 'axis_tolerance'

export interface SgiGoal {
  readonly goalMin: number | null
  readonly goalMax: number | null
  readonly goalAxis: number | null
  readonly goalTolerance: number | null
  readonly marginMode: SgiMarginMode | null
}

export interface SgiIndicator {
  readonly id: string
  readonly nome: string
  readonly categoriaId: string
  readonly categoria: string
  readonly unidade: string | null
  readonly tipo: 'currency' | 'percentage' | 'number' | null
  /** `yyyy-MM` do cadastro; `null` = legado sem data (não entra na cobrança, como no SGI). */
  readonly criadoMes: string | null
  readonly focusType: SgiFocusType | null
  readonly meta: SgiGoal
  /** Metas por mês (`sgi_indicator_monthly_goals`), quando houver. */
  readonly metasMensais: Readonly<Record<string, SgiGoal>>
  /** Valor lançado por mês `yyyy-MM`. */
  readonly valores: Readonly<Record<string, number>>
}

export type SgiLaunchStatus = 'lancado' | 'pendente' | 'atrasado' | 'sem_lancamento' | 'nao_fechado' | 'nao_devido'

export const SGI_LAUNCH_LABEL: Record<SgiLaunchStatus, string> = {
  lancado: 'Lançado',
  pendente: 'Pendente',
  atrasado: 'Atrasado',
  sem_lancamento: 'Sem lançamento (não cobrado)',
  nao_fechado: 'Mês ainda não fechou',
  nao_devido: 'Não se aplica',
}

/** Mês de referência dos lançamentos: o anterior ao atual (o mês corrente ainda não fechou). */
export function sgiLaunchMonth(today: string): string {
  return shiftMonth(today.slice(0, 7), -1)
}

/** Os `n` meses fechados até o de referência, do mais antigo ao mais novo. */
export function sgiMonths(today: string, n: number): string[] {
  const last = sgiLaunchMonth(today)
  return Array.from({ length: n }, (_, i) => shiftMonth(last, i - n + 1))
}

/** Os 12 meses do ano do mês de referência (janeiro a dezembro), para ver o ano inteiro. */
export function sgiYearMonths(today: string): string[] {
  const year = sgiLaunchMonth(today).slice(0, 4)
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`)
}

/**
 * Status do lançamento de um mês.
 *
 * - Devido: indicador criado antes do mês (criado em M conta a partir de M+1);
 *   sem data de criação não é cobrado. Mês corrente ou futuro: "não fechou".
 * - Lançado: há valor no mês.
 * - Pendente: é o mês de referência e ainda não tem valor.
 * - Atrasado: um dos 2 meses antes do de referência (janela de 3), a partir
 *   de 2026-01, sem valor.
 * - Sem lançamento: devido, sem valor, mas fora da janela — mostra, não cobra.
 */
export function sgiLaunchStatus(indicator: Pick<SgiIndicator, 'criadoMes' | 'valores'>, month: string, today: string): SgiLaunchStatus {
  if (month in indicator.valores) return 'lancado'
  const ref = sgiLaunchMonth(today)
  if (month > ref) return 'nao_fechado'
  if (!indicator.criadoMes || indicator.criadoMes >= month) return 'nao_devido'
  if (month === ref) return 'pendente'
  if (month < SGI_OVERDUE_START_MONTH) return 'nao_devido'
  return month >= shiftMonth(ref, -(SGI_CHARGE_MONTHS - 1)) ? 'atrasado' : 'sem_lancamento'
}

export type SgiGoalCheck = 'na_meta' | 'fora_da_meta' | 'sem_meta'

export const SGI_GOAL_LABEL: Record<SgiGoalCheck, string> = {
  na_meta: 'Na meta',
  fora_da_meta: 'Fora da meta',
  sem_meta: 'Sem meta',
}

export function sgiGoalFor(indicator: Pick<SgiIndicator, 'meta' | 'metasMensais'>, month: string): SgiGoal {
  return indicator.metasMensais[month] ?? indicator.meta
}

/** Valor do mês contra a meta, com a mesma conta do SGI ("manter" = na meta). */
export function sgiGoalCheck(indicator: Pick<SgiIndicator, 'focusType' | 'meta' | 'metasMensais'>, month: string, value: number): SgiGoalCheck {
  const g = sgiGoalFor(indicator, month)
  const tol = g.goalTolerance
  switch (indicator.focusType) {
    case 'crescente': {
      const target = g.goalAxis ?? g.goalMin
      if (target === null) return 'sem_meta'
      return value >= target - (tol && tol > 0 ? tol : 0) ? 'na_meta' : 'fora_da_meta'
    }
    case 'decrescente': {
      const target = g.goalAxis ?? g.goalMax
      if (target === null) return 'sem_meta'
      return value <= target + (tol && tol > 0 ? tol : 0) ? 'na_meta' : 'fora_da_meta'
    }
    case 'margem': {
      if (g.marginMode === 'axis_tolerance') {
        if (g.goalAxis === null || tol === null) return 'sem_meta'
        return value >= g.goalAxis - tol && value <= g.goalAxis + tol ? 'na_meta' : 'fora_da_meta'
      }
      if (g.goalMin === null || g.goalMax === null) return 'sem_meta'
      return value >= g.goalMin && value <= g.goalMax ? 'na_meta' : 'fora_da_meta'
    }
    case 'eixo': {
      if (g.goalAxis === null) return 'sem_meta'
      const t = tol ?? 0
      return value >= g.goalAxis - t && value <= g.goalAxis + t ? 'na_meta' : 'fora_da_meta'
    }
    default:
      return 'sem_meta'
  }
}

/** Meta em uma linha ("≥ 95", "≤ 3", "10 a 20", "50 ± 5"), ou `null`. */
export function sgiGoalText(indicator: Pick<SgiIndicator, 'focusType' | 'meta' | 'metasMensais'>, month: string, fmt: (n: number) => string): string | null {
  const g = sgiGoalFor(indicator, month)
  switch (indicator.focusType) {
    case 'crescente': {
      const t = g.goalAxis ?? g.goalMin
      return t === null ? null : `≥ ${fmt(t)}`
    }
    case 'decrescente': {
      const t = g.goalAxis ?? g.goalMax
      return t === null ? null : `≤ ${fmt(t)}`
    }
    case 'margem':
      if (g.marginMode === 'axis_tolerance') return g.goalAxis === null || g.goalTolerance === null ? null : `${fmt(g.goalAxis)} ± ${fmt(g.goalTolerance)}`
      return g.goalMin === null || g.goalMax === null ? null : `${fmt(g.goalMin)} a ${fmt(g.goalMax)}`
    case 'eixo':
      return g.goalAxis === null ? null : g.goalTolerance ? `${fmt(g.goalAxis)} ± ${fmt(g.goalTolerance)}` : fmt(g.goalAxis)
    default:
      return null
  }
}

export interface SgiLaunchCount {
  readonly lancados: number
  readonly pendentes: number
  readonly atrasados: number
}

/** Contagem dos meses (`months`) de um conjunto de indicadores. */
export function sgiLaunchCount(indicators: readonly SgiIndicator[], months: readonly string[], today: string): SgiLaunchCount {
  let lancados = 0
  let pendentes = 0
  let atrasados = 0
  for (const i of indicators) {
    for (const m of months) {
      const s = sgiLaunchStatus(i, m, today)
      if (s === 'lancado') lancados += 1
      else if (s === 'pendente') pendentes += 1
      else if (s === 'atrasado') atrasados += 1
    }
  }
  return { lancados, pendentes, atrasados }
}

/** Meses cobrados sem lançamento de um indicador (pendente + atrasados, só na janela de 3 meses). */
export function sgiMissingMonths(indicator: Pick<SgiIndicator, 'criadoMes' | 'valores'>, today: string): string[] {
  const ref = sgiLaunchMonth(today)
  const out: string[] = []
  for (let m = SGI_OVERDUE_START_MONTH; m <= ref; m = shiftMonth(m, 1)) {
    const s = sgiLaunchStatus(indicator, m, today)
    if (s === 'pendente' || s === 'atrasado') out.push(m)
  }
  return out
}

function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
}

/**
 * Categoria do SGI → código da área no Supply. Nomes do SGI (2026-10):
 * Gestão Supply, PCP, Compras, Manutenção, Estoque. Desconhecida → `null`
 * (aparece como "Sem área no Supply" para o gestor).
 */
const CATEGORY_TO_AREA: Record<string, string> = {
  'GESTAO SUPPLY': 'SUPPLY',
  SUPPLY: 'SUPPLY',
  PCP: 'PCP/ENG',
  'PCP ENG': 'PCP/ENG',
  'PCP ENGENHARIA': 'PCP/ENG',
  COMPRAS: 'COMPRAS',
  MANUTENCAO: 'MANUTENÇÃO',
  ESTOQUE: 'ESTOQUE',
}

export function supplyAreaCodeOfCategory(categoria: string): string | null {
  return CATEGORY_TO_AREA[normalize(categoria)] ?? null
}

export interface SgiAreaSummary {
  /** Indicadores cobrados no mês de referência (lançados + pendentes). */
  readonly devidos: number
  readonly lancados: number
  readonly pendentes: number
  /** Meses atrasados (indicador × mês) nos meses cobrados antes do de referência. */
  readonly atrasados: number
}

/** Resumo de uma área no mês de referência: "X de Y lançados · Z atrasados". */
export function sgiAreaSummary(indicators: readonly SgiIndicator[], today: string): SgiAreaSummary {
  const ref = sgiLaunchMonth(today)
  let lancados = 0
  let pendentes = 0
  for (const i of indicators) {
    const s = sgiLaunchStatus(i, ref, today)
    if (s === 'pendente') pendentes += 1
    // Lançado conta se o mês era devido; lançamento "a mais" (antes de cobrar) não entra na base.
    else if (s === 'lancado' && sgiLaunchStatus({ criadoMes: i.criadoMes, valores: {} }, ref, today) === 'pendente') lancados += 1
  }
  const atrasados = sgiLaunchCount(indicators, sgiMonths(today, SGI_CHARGE_MONTHS), today).atrasados
  return { devidos: lancados + pendentes, lancados, pendentes, atrasados }
}
